'use client';

import { useState, useTransition, useMemo, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import debounce from 'lodash.debounce';
import { searchStocks } from '@/app/actions/search/search-stocks';
import styles from './styles/HomepageSearch.module.css';
import { cn } from '@/lib/utils';

interface Stock {
  t: string; // ticker
  n1: string; // company name
}

interface PriceData {
  ticker: string;
  short_name: string;
  r: number;
  p: number;
}

export function HomepageSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Stock[]>([]);
  const [isPending, startTransition] = useTransition();
  const [isFocused, setIsFocused] = useState(false);
  const [priceData, setPriceData] = useState<Record<string, PriceData[]>>({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const router = useRouter();

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length > 1) {
          startTransition(async () => {
            const data = await searchStocks(value);
            setResults(data);
          });
        } else {
          setResults([]);
        }
      }, 300),
    [startTransition]
  );

  useEffect(() => {
    debouncedSearch(query);

    return () => {
      debouncedSearch.cancel();
    };
  }, [query, debouncedSearch]);

  // Fetch prices after search results are loaded
  useEffect(() => {
    if (results.length > 0) {
      const tickersToFetch = results
        .map(stock => stock.t)
        .filter(ticker => !priceData[ticker]);

      if (tickersToFetch.length === 0) return;

      setLoadingPrices(true);

      const fetchPrices = async () => {
        try {
          const response = await fetch('/api/stock/latest-prices', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tickers: tickersToFetch }),
            signal: AbortSignal.timeout(5000)
          }).catch(() => null);

          if (!response) {
            setLoadingPrices(false);
            return;
          }

          const data = await response.json().catch(() => ({ getLatestPrice: [] }));
          const { getLatestPrice: latestPriceData = [] } = data || {};

          interface LatestPriceItem {
            ticker: string;
            short_name: string;
            r: number;
            p: number;
          }

          const newPriceData: Record<string, PriceData[]> = latestPriceData.reduce(
            (acc: Record<string, PriceData[]>, item: LatestPriceItem) => {
              if (!acc[item.ticker]) {
                acc[item.ticker] = [];
              }
              acc[item.ticker].push(item);
              return acc;
            },
            {} as Record<string, PriceData[]>
          );

          setPriceData(current => ({ ...current, ...newPriceData }));
        } catch (error) {
          console.error('Error fetching price data:', error);
        } finally {
          setLoadingPrices(false);
        }
      };

      fetchPrices();
    }
  }, [results]);

  const handleResultClick = (ticker: string) => {
    router.push(`/stock/${ticker}`);
    setQuery('');
    setResults([]);
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchWrapper}>
        <svg
          className={styles.searchIcon}
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input
          type="search"
          placeholder="Search for company name or ticker..."
          value={query}
          className={cn(styles.searchInput)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setTimeout(() => {
              setIsFocused(false);
            }, 200);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
        />
      </div>

      {query.length > 1 && isFocused && (
        <div className={styles.searchResults}>
          {isPending ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner}></div>
              <span>Searching...</span>
            </div>
          ) : results.length === 0 ? (
            <div className={styles.emptyState}>No results found</div>
          ) : (
            <ul className={styles.resultsList} role="listbox">
              {results.slice(0, 8).map((stock) => {
                const stockPrices = priceData[stock.t];
                let isPositive = true; // Default to positive if no price data

                if (stockPrices && stockPrices.length >= 2) {
                  const [current, previous] = stockPrices;
                  isPositive = current.p >= previous.p;
                }

                return (
                  <li
                    key={stock.t}
                    role="option"
                    aria-selected="false"
                    className={styles.resultItem}
                    onMouseDown={() => handleResultClick(stock.t)}
                  >
                    <div className={styles.companyName}>{stock.n1}</div>

                    <div className={`${styles.stockSymbol} ${isPositive ? styles.positiveStock : styles.negativeStock}`}>
                      {stock.t}
                      <span className={styles.trendIndicator}>
                        {isPositive ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 8l-9 9h18l-9-9z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 16l-9-9h18l-9 9z" />
                          </svg>
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}